import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { teamsApi } from '@/lib/api';

interface Team {
  id: string;
  name: string;
  slug: string;
}

interface TeamMember {
  userId: string;
  name: string;
  email: string;
  role: string;
}

interface TeamState {
  teams: Team[];
  currentTeamId: string | null;
  currentTeam: Team | null;
  members: TeamMember[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTeams: () => Promise<void>;
  setCurrentTeam: (teamId: string) => void;
  createTeam: (name: string, slug: string) => Promise<Team>;
  fetchMembers: (teamId: string) => Promise<void>;
  reset: () => void;
}

export const useTeamStore = create<TeamState>()(
  persist(
    (set, get) => ({
      teams: [],
      currentTeamId: null,
      currentTeam: null,
      members: [],
      isLoading: false,
      error: null,

      fetchTeams: async () => {
        set({ isLoading: true, error: null });
        try {
          const { teams } = await teamsApi.list();
          const currentTeamId = get().currentTeamId;

          // If no current team selected, or current team no longer exists, select first one
          let selectedTeamId = currentTeamId;
          if (!selectedTeamId || !teams.find((t) => t.id === selectedTeamId)) {
            selectedTeamId = teams[0]?.id || null;
          }

          const currentTeam = teams.find((t) => t.id === selectedTeamId) || null;

          set({
            teams,
            currentTeamId: selectedTeamId,
            currentTeam,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch teams',
            isLoading: false,
          });
        }
      },

      setCurrentTeam: (teamId: string) => {
        const { teams } = get();
        const team = teams.find((t) => t.id === teamId);
        if (team) {
          set({ currentTeamId: teamId, currentTeam: team });
        }
      },

      createTeam: async (name: string, slug: string) => {
        set({ isLoading: true, error: null });
        try {
          const { team } = await teamsApi.create({ name, slug });
          const teams = [...get().teams, team];
          set({
            teams,
            currentTeamId: team.id,
            currentTeam: team,
            isLoading: false,
          });
          return team;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create team',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchMembers: async (teamId: string) => {
        try {
          const { members } = await teamsApi.getMembers(teamId);
          set({ members });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch members',
          });
        }
      },

      reset: () => {
        set({
          teams: [],
          currentTeamId: null,
          currentTeam: null,
          members: [],
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'piano-team',
      partialize: (state) => ({
        currentTeamId: state.currentTeamId,
      }),
    }
  )
);
